// components/Home.js
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";

export default function Home({ websiteId, sections = [], basePath = "", newsSection }) {
  const [page, setPage] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });

  // Prefer page-like sections first to minimize 404s
  const ordered = useMemo(() => {
    const slug = (s) => (s.slug || "").toLowerCase();
    const type = (s) => (s.type || "").toLowerCase();
    const isNews = (s) => type(s) === "news" || slug(s) === "nyheter";
    const isPages = (s) => type(s) === "pages" || slug(s) === "sidor";

    const pri1 = sections.filter((s) => slug(s) === "sidor");     // exact "sidor"
    const pri2 = sections.filter((s) => isPages(s) && slug(s) !== "sidor");
    const pri3 = sections.filter((s) => !isNews(s) && !isPages(s));
    const out = [...pri1, ...pri2, ...pri3];
    const seen = new Set();
    return out.filter((s) => (seen.has(s._id) ? false : (seen.add(s._id), true)));
  }, [sections]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) Try canonical: /resolve-by-slug/:websiteId/sidor/hem
        try {
          const { item } = await api.resolveBySlugs(websiteId, "sidor", "hem");
          if (!cancelled) {
            setPage(item);
            setState({ loading: false, error: null });
          }
          return;
        } catch (_) {
          // ignore, we'll search
        }

        // 2) Search: list items in likely sections and look for link === "hem"
        const lists = await Promise.allSettled(
          ordered.map((s) => api.itemsBySection(s._id))
        );

        let match = null;
        for (let i = 0; i < lists.length; i++) {
          const r = lists[i];
          if (r.status === "fulfilled" && Array.isArray(r.value)) {
            const found = r.value.find((x) => (x.link || "").toLowerCase() === "hem");
            if (found) { match = found; break; }
          }
        }

        if (match) {
          const full = await api.itemById(match._id);
          if (!cancelled) {
            setPage(full);
            setState({ loading: false, error: null });
          }
          return;
        }

        if (!cancelled) setState({ loading: false, error: "NOT_FOUND" });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message || "Fel" });
      }
    })();
    return () => { cancelled = true; };
  }, [websiteId, ordered]);

  if (state.loading) return <div className="p-8">Laddar startsida…</div>;

  // If we didn’t find "hem", go to news if available; else show a small message
  if (!page) {
    if (newsSection) return <Navigate to={`${basePath}/nyheter`.replace(/\/\//g, "/")} />;
    return <div className="p-8">Ingen sida “Hem” hittades.</div>;
  }

  return (
    <section className="p-8 min-h-[50vh] w-fit mx-auto lg:pb-16 lg:px-48 portrait:min-h-[57vh]">
      <h2 className="text-2xl font-bold mb-3">{page.name}</h2>
      <div dangerouslySetInnerHTML={{ __html: page.content || "" }} />
    </section>
  );
}

